import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import WorkshopStepWrapper from '../WorkshopStepWrapper';
import { useWorkshopProgress } from '@/contexts/WorkshopProgressContext';
import { CheckCircle, Trophy } from 'lucide-react';

export const WorkshopCTA: React.FC = () => {
  const { dispatch } = useWorkshopProgress();

  const handleCompleteWorkshop = () => {
    dispatch({ type: 'COMPLETE_WORKSHOP' });
  };

  return (
    <WorkshopStepWrapper
      step="cta"
      title="Workshop Complete - Skills Validated!"
      description="You've experienced professional AI transformation in action"
      showSkillsPreview={false}
      autoMarkComplete={true}
    >
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-purple-500 mx-auto mb-4 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Workshop Complete - Skills Validated!
        </h2>
        <p className="text-xl text-gray-600">
          You've experienced professional AI transformation in action
        </p>
      </div>

      {/* What You Accomplished */}
      <Card className="mb-8 bg-gradient-to-r from-green-50 to-purple-50 border-green-200">
        <CardHeader>
          <CardTitle className="text-center">What You Just Accomplished</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <strong>Hands-On Practice:</strong> Actively improved real AI content using professional techniques
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <strong>Real-Time Coaching:</strong> Experienced Lyra's guidance through complex AI quality challenges
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <strong>Tangible Results:</strong> Created content you can actually use in your nonprofit work
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <strong>Skill Validation:</strong> Demonstrated 5 core AI mastery competencies with measurable outcomes
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Training CTA */}
      <div className="text-center space-y-6">
        <h3 className="text-2xl font-semibold text-gray-900">Ready for Complete Professional Transformation?</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          This 5-minute workshop showed you the foundation. The complete Lyra AI Mentor experience includes
          40+ lessons, 135+ professional prompts, and systematic training across all aspects of nonprofit
          AI mastery - from fundraising to program management to stakeholder communication.
        </p>

        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-3xl mx-auto">
          <div className="flex items-start space-x-6">
            <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
              <SupabaseIcon
                iconPath="lyra-avatar.png"
                alt="Lyra"
                size="lg"
                className="w-12 h-12"
              />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-xl font-bold text-purple-800 mb-3">Join the Complete Journey</h4>
              <p className="text-gray-700 mb-4">
                "You've proven you can master AI quality control. Now let's scale these skills across
                your entire mission. Join hundreds of nonprofit professionals who've transformed their
                impact through systematic AI mastery."
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>40+ hands-on lessons</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>135+ professional prompts</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Multi-character journey</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Real-time AI coaching</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Button
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-green-600 hover:from-purple-700 hover:to-green-700 text-lg px-8 py-3"
            onClick={() => {
              handleCompleteWorkshop();
              window.open('https://www.hellolyra.app', '_blank');
            }}
          >
            <Trophy className="w-5 h-5 mr-2" />
            Start Complete AI Transformation
          </Button>

          <div className="text-sm text-gray-500">
            Full professional training • Multiple expert characters • Complete workflow mastery
          </div>
        </div>
      </div>
    </WorkshopStepWrapper>
  );
};

export default WorkshopCTA;